// src/pages/Settings.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI, contactAPI, authAPI } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  CreditCard as CreditCardIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Pending as PendingIcon,
  Sms as SmsIcon,
  Call as CallIcon,
  Warning as WarningIcon,
  Summarize as SummarizeIcon,
  Security as SecurityIcon,
  Add as AddIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';

// Phone number formatting
const formatPhoneInput = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 0) return '';
  if (numbers.length <= 3) return `(${numbers}`;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

// Password requirements
const passwordRequirements = [
  { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { label: 'Contains uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'Contains lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'Contains a number', test: (pw) => /[0-9]/.test(pw) },
  { label: 'Contains a special character (!@#$%^&*...)', test: (pw) => /[!@#$%^&*(),.?":{}|<>_\-+=]/.test(pw) },
];

// Uniform card dimensions
const CARD_MIN_HEIGHT = 320;

const Settings = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  
  // Profile form
  const [profileData, setProfileData] = useState({ name: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  
  // Email change state (multi-step: input -> code -> done)
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailStep, setEmailStep] = useState('input'); // 'input', 'code', 'done'
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailDeliveryMethod, setEmailDeliveryMethod] = useState('sms');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailMaskedPhone, setEmailMaskedPhone] = useState('');
  const [emailChangeSaving, setEmailChangeSaving] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState('');
  const [emailChangeSuccess, setEmailChangeSuccess] = useState('');
  const [pendingEmailChange, setPendingEmailChange] = useState(null);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  
  // Phone change state
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneStep, setPhoneStep] = useState('request'); // 'request' or 'verify'
  const [newPhone, setNewPhone] = useState('');
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [phoneChangeSaving, setPhoneChangeSaving] = useState(false);
  const [phoneChangeError, setPhoneChangeError] = useState('');
  const [phoneChangeSuccess, setPhoneChangeSuccess] = useState('');
  const [pendingPhoneChange, setPendingPhoneChange] = useState(null);
  
  // Password form
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailHighRisk: true,
    emailDailySummary: false,
    smsHighRisk: true,
    smsAllMessages: false,
    alertOnMoneySent: true,
    alertOnMoneySentChannel: 'both',
    alertOnPiiDisclosed: true,
    alertChannel: 'both',
    summaryFrequency: 'off',
    summaryChannel: 'email',
    summaryDay: 1,
    summarySendIfNoActivity: false,
  });
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationSuccess, setNotificationSuccess] = useState('');
  const [notificationError, setNotificationError] = useState('');
  
  // Billing portal
  const [billingLoading, setBillingLoading] = useState(false);

  // Add Person dialog (for client_solo users)
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addPersonForm, setAddPersonForm] = useState({ name: '', phone: '', email: '' });
  const [addPersonError, setAddPersonError] = useState('');
  const [addPersonPhoneError, setAddPersonPhoneError] = useState('');
  const [addPersonEmailError, setAddPersonEmailError] = useState('');
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [addPersonSubmitting, setAddPersonSubmitting] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const phoneCheckController = useRef(null);
  const emailCheckController = useRef(null);

  // Per-client mute toggles (for self users)
  const [clientList, setClientList] = useState([]);
  const [mutedClients, setMutedClients] = useState([]);

  // Password validation
  const passwordValidation = passwordRequirements.map((req) => ({
    ...req,
    met: req.test(passwordData.newPassword),
  }));
  const allRequirementsMet = passwordValidation.every((req) => req.met);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
      });
      if (user.notificationPreferences) {
        setNotificationPrefs({
          emailHighRisk: user.notificationPreferences.email_high_risk ?? true,
          emailDailySummary: user.notificationPreferences.email_daily_summary ?? false,
          smsHighRisk: user.notificationPreferences.sms_high_risk ?? true,
          smsAllMessages: user.notificationPreferences.sms_all_messages ?? false,
          alertOnMoneySent: user.notificationPreferences.alert_on_money_sent ?? true,
          alertOnMoneySentChannel: user.notificationPreferences.alert_on_money_sent_channel ?? 'both',
          alertOnPiiDisclosed: user.notificationPreferences.alert_on_pii_disclosed ?? true,
          alertChannel: user.notificationPreferences.alert_channel || user.notificationPreferences.alert_on_money_sent_channel || 'both',
          summaryFrequency: user.notificationPreferences.summary_frequency ?? 'off',
          summaryChannel: user.notificationPreferences.summary_channel ?? 'email',
          summaryDay: user.notificationPreferences.summary_day ?? 1,
          summarySendIfNoActivity: user.notificationPreferences.summary_send_if_no_activity ?? false,
        });
        setMutedClients(user.notificationPreferences.muted_clients || []);
      }
      // Load client list for per-client mute toggles (self users only)
      if (user.accountType === 'self') {
        userAPI.getClients().then(res => {
          setClientList(res.data.clients || []);
        }).catch(() => {});
      }
    }
  }, [user]);

  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return formatPhoneInput(digits.slice(1));
    }
    return formatPhoneInput(digits);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [subRes, pendingRes] = await Promise.all([
        userAPI.getSubscription().catch(() => ({ data: { subscription: null } })),
        contactAPI.getPendingChanges().catch(() => ({ data: { pendingChanges: [] } })),
      ]);
      setSubscription(subRes.data.subscription);
      
      const pending = pendingRes.data.pendingChanges || [];
      setPendingEmailChange(pending.find(p => p.type === 'email') || null);
      setPendingPhoneChange(pending.find(p => p.type === 'phone') || null);
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Profile handlers
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
    setProfileError('');
    setProfileSuccess('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      await userAPI.updateProfile({ name: profileData.name });
      setProfileSuccess('Profile updated successfully');
      if (refreshUser) refreshUser();
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  // Email change handlers
  const resetEmailDialog = () => {
    setEmailStep('input');
    setNewEmail('');
    setEmailPassword('');
    setEmailDeliveryMethod('sms');
    setEmailVerificationCode('');
    setEmailMaskedPhone('');
    setEmailChangeError('');
    setShowEmailPassword(false);
  };

  const handleEmailChangeRequest = async (method) => {
    const deliveryMethod = method || emailDeliveryMethod;
    if (!newEmail) {
      setEmailChangeError('Please enter a new email address');
      return;
    }
    if (!emailPassword) {
      setEmailChangeError('Please enter your current password');
      return;
    }

    setEmailChangeSaving(true);
    setEmailChangeError('');

    try {
      const response = await authAPI.requestEmailChange({
        newEmail,
        password: emailPassword,
        deliveryMethod,
      });
      setEmailMaskedPhone(response.data.maskedPhone || '');
      setEmailStep('code');
    } catch (err) {
      setEmailChangeError(err.response?.data?.error || 'Failed to request email change');
    } finally {
      setEmailChangeSaving(false);
    }
  };

  const handleEmailResendCode = async (method) => {
    setEmailChangeSaving(true);
    setEmailChangeError('');
    setEmailDeliveryMethod(method);
    
    try {
      const response = await authAPI.requestEmailChange({
        newEmail,
        password: emailPassword,
        deliveryMethod: method,
      });
      setEmailMaskedPhone(response.data.maskedPhone || '');
    } catch (err) {
      setEmailChangeError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setEmailChangeSaving(false);
    }
  };

  const handleEmailCodeVerify = async () => {
    if (emailVerificationCode.length !== 6) {
      setEmailChangeError('Please enter the 6-digit code');
      return;
    }

    setEmailChangeSaving(true);
    setEmailChangeError('');

    try {
      await authAPI.verifyEmailChange({ code: emailVerificationCode });
      setEmailStep('done');
      loadData();
    } catch (err) {
      setEmailChangeError(err.response?.data?.error || 'Invalid code');
    } finally {
      setEmailChangeSaving(false);
    }
  };

  const handleCancelEmailChange = async () => {
    try {
      await contactAPI.cancelPendingChange('email');
      setPendingEmailChange(null);
      setEmailChangeSuccess('Email change cancelled');
    } catch (err) {
      console.error('Error cancelling email change:', err);
    }
  };

  const handleResendEmailVerification = async () => {
    try {
      await authAPI.resendEmailVerification();
      setEmailChangeSuccess('Verification email resent');
    } catch (err) {
      console.error('Error resending verification:', err);
    }
  };

  // Phone change handlers
  const handlePhoneChangeRequest = async () => {
    if (!newPhone) {
      setPhoneChangeError('Please enter a new phone number');
      return;
    }

    setPhoneChangeSaving(true);
    setPhoneChangeError('');

    try {
      await contactAPI.requestPhoneChange(newPhone.replace(/\D/g, ''));
      setPhoneStep('verify');
    } catch (err) {
      console.error('Phone change request failed:', err.response?.data || err.message);
      setPhoneChangeError(err.response?.data?.error || 'Failed to send verification code');
    } finally {
      setPhoneChangeSaving(false);
    }
  };

  const handlePhoneVerify = async () => {
    if (phoneVerificationCode.length !== 6) {
      setPhoneChangeError('Please enter the 6-digit code');
      return;
    }

    setPhoneChangeSaving(true);
    setPhoneChangeError('');

    try {
      await contactAPI.verifyPhoneChange(phoneVerificationCode);
      setPhoneDialogOpen(false);
      setPhoneStep('request');
      setNewPhone('');
      setPhoneVerificationCode('');
      setPhoneChangeSuccess('Phone number updated successfully');
      setPendingPhoneChange(null);
      if (refreshUser) refreshUser();
    } catch (err) {
      console.error('Phone verification failed:', err.response?.data || err.message);
      setPhoneChangeError(err.response?.data?.error || 'Invalid code');
    } finally {
      setPhoneChangeSaving(false);
    }
  };

  const handleResendPhoneCode = async () => {
    setPhoneChangeSaving(true);
    try {
      await contactAPI.requestPhoneChange(
        (pendingPhoneChange?.newValue || newPhone).replace(/\D/g, '')
      );
    } catch (err) {
      setPhoneChangeError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setPhoneChangeSaving(false);
    }
  };

  const handleCancelPhoneChange = async () => {
    try {
      await contactAPI.cancelPendingChange('phone');
      setPendingPhoneChange(null);
      setPhoneChangeSuccess('Phone change cancelled');
    } catch (err) {
      console.error('Error cancelling phone change:', err);
    }
  };

  // Password handlers
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      setPasswordError('Password does not meet all requirements');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordSaving(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setPasswordSuccess('Password updated successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Notification handlers
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotificationPrefs({ ...notificationPrefs, [name]: checked });
    setNotificationSuccess('');
    setNotificationError('');
  };

  const handleNotificationSubmit = async () => {
    setNotificationSaving(true);
    setNotificationError('');
    setNotificationSuccess('');

    try {
      await userAPI.updateNotificationPreferences({
        email_high_risk: notificationPrefs.emailHighRisk,
        email_daily_summary: notificationPrefs.emailDailySummary,
        sms_high_risk: notificationPrefs.smsHighRisk,
        sms_all_messages: notificationPrefs.smsAllMessages,
        alert_on_money_sent: notificationPrefs.alertOnMoneySent,
        alert_on_money_sent_channel: notificationPrefs.alertChannel, // legacy compat
        alert_on_pii_disclosed: notificationPrefs.alertOnPiiDisclosed,
        alert_channel: notificationPrefs.alertChannel,
        summary_frequency: notificationPrefs.summaryFrequency,
        summary_channel: notificationPrefs.summaryChannel,
        summary_day: notificationPrefs.summaryDay,
        summary_send_if_no_activity: notificationPrefs.summarySendIfNoActivity,
      });

      setNotificationSuccess('Preferences saved');
      if (refreshUser) refreshUser();
    } catch (err) {
      setNotificationError(err.response?.data?.error || 'Failed to save preferences');
    } finally {
      setNotificationSaving(false);
    }
  };

  // Billing portal handler
  const [billingError, setBillingError] = useState('');
  const handleManageBilling = async () => {
    setBillingLoading(true);
    setBillingError('');
    try {
      const response = await userAPI.createBillingPortalSession();
      if (response.data.url) {
        // Use location.href instead of window.open to avoid popup blockers
        // (window.open in an async callback is blocked by most browsers)
        window.location.href = response.data.url;
      } else {
        setBillingError('No billing portal URL returned. Please try again.');
      }
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setBillingError(err.response?.data?.error || 'Failed to open billing portal. Please try again.');
    } finally {
      setBillingLoading(false);
    }
  };

  // Add Person handlers (for client_solo users)
  const handleAddPersonFormChange = (e) => {
    let { name, value } = e.target;
    if (name === 'phone') {
      value = formatPhoneInput(value);
      setAddPersonPhoneError('');
    }
    if (name === 'email') {
      setAddPersonEmailError('');
    }
    setAddPersonForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddPersonPhoneBlur = async () => {
    const digits = addPersonForm.phone.replace(/\D/g, '');
    if (digits.length < 10) return;
    if (phoneCheckController.current) phoneCheckController.current.abort();
    phoneCheckController.current = new AbortController();
    setCheckingPhone(true);
    setAddPersonPhoneError('');
    try {
      const formattedPhone = digits.length === 10 ? `+1${digits}` : `+${digits}`;
      const res = await userAPI.checkClientPhone(formattedPhone);
      if (!res.data.available) {
        setAddPersonPhoneError(res.data.message || 'This number is already enrolled.');
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        console.error('Phone check error:', err);
      }
    } finally {
      setCheckingPhone(false);
    }
  };

  const handleAddPersonEmailBlur = async () => {
    const email = addPersonForm.email.trim();
    if (!email || !email.includes('@')) return;
    if (emailCheckController.current) emailCheckController.current.abort();
    emailCheckController.current = new AbortController();
    setCheckingEmail(true);
    setAddPersonEmailError('');
    try {
      const res = await userAPI.checkClientEmail(email);
      if (!res.data.available) {
        setAddPersonEmailError(res.data.message || 'This email is already enrolled.');
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        console.error('Email check error:', err);
      }
    } finally {
      setCheckingEmail(false);
    }
  };

  const submitAddPerson = async (data) => {
    setAddPersonSubmitting(true);
    setAddPersonError('');
    try {
      const response = await userAPI.addClient(data);
      setAddPersonOpen(false);
      setUpgradeInfo(null);
      setPendingData(null);
      if (response.data.accountTypeChanged) {
        await refreshUser();
        navigate('/people');
      }
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'UPGRADE_REQUIRED') {
        setUpgradeInfo(err.response.data.upgrade);
        setPendingData(data);
        return;
      }
      setAddPersonError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
    } finally {
      setAddPersonSubmitting(false);
    }
  };

  const handleAddPersonSubmit = async () => {
    setAddPersonError('');
    if (!addPersonForm.name.trim()) {
      setAddPersonError('Please enter a name');
      return;
    }
    const hasPhone = addPersonForm.phone.trim().length > 0;
    const hasEmail = addPersonForm.email.trim().length > 0;
    if (!hasPhone && !hasEmail) {
      setAddPersonError('Please provide a phone number or email address (or both)');
      return;
    }
    let formattedPhone;
    if (hasPhone) {
      const phoneDigits = addPersonForm.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        setAddPersonError('Please enter a valid 10-digit phone number');
        return;
      }
      if (addPersonPhoneError) {
        setAddPersonError(addPersonPhoneError);
        return;
      }
      formattedPhone = phoneDigits.length === 10 ? `+1${phoneDigits}` : `+${phoneDigits}`;
    }
    if (hasEmail && addPersonEmailError) {
      setAddPersonError(addPersonEmailError);
      return;
    }
    if (hasEmail && !addPersonForm.email.trim().includes('@')) {
      setAddPersonError('Please enter a valid email address');
      return;
    }
    await submitAddPerson({
      name: addPersonForm.name.trim(),
      phone: formattedPhone || undefined,
      email: hasEmail ? addPersonForm.email.trim() : undefined,
    });
  };

  const handleUpgradeConfirm = async () => {
    if (!pendingData) return;
    await submitAddPerson({ ...pendingData, confirmUpgrade: true });
  };

  const handleAddPersonClose = () => {
    setAddPersonOpen(false);
    setAddPersonForm({ name: '', phone: '', email: '' });
    setAddPersonError('');
    setAddPersonPhoneError('');
    setAddPersonEmailError('');
    setUpgradeInfo(null);
    setPendingData(null);
  };

  // Per-client mute handler
  const handleMuteToggle = async (clientId) => {
    const newMuted = mutedClients.includes(clientId)
      ? mutedClients.filter(id => id !== clientId)
      : [...mutedClients, clientId];
    setMutedClients(newMuted);
    try {
      await userAPI.updateNotificationPreferences({ muted_clients: newMuted });
    } catch (err) {
      // Revert on failure
      setMutedClients(mutedClients);
      console.error('Failed to update muted clients:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Uniform card style
  const cardStyle = {
    p: 3,
    height: '100%',
    minHeight: CARD_MIN_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="h4" gutterBottom sx={{ alignSelf: 'flex-start', width: '100%', maxWidth: 900 }}>
        Account Settings
      </Typography>

      {/* Card Grid Container */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
          },
          gap: 3,
          width: '100%',
          maxWidth: 900,
        }}
      >
        {/* Protect a Loved One Card — solo users only */}
        {user?.accountType === 'client_solo' && (
          <Paper sx={{ ...cardStyle, gridColumn: { xs: '1', md: '1 / -1' }, minHeight: 'auto', background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FavoriteIcon sx={{ fontSize: 40, color: '#7c3aed' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Protect a Loved One
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add a family member or friend to monitor their incoming calls, texts, and emails for scams. You'll get alerts when we detect something suspicious.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddPersonForm({ name: '', phone: '', email: '' });
                  setAddPersonError('');
                  setAddPersonPhoneError('');
                  setUpgradeInfo(null);
                  setPendingData(null);
                  setAddPersonOpen(true);
                }}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Add a Person
              </Button>
            </Box>
          </Paper>
        )}

        {/* Profile Card */}
        <Paper sx={cardStyle}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            Profile Information
          </Typography>

          <Box component="form" onSubmit={handleProfileSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {profileError && <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>}
            {profileSuccess && <Alert severity="success" sx={{ mb: 2 }}>{profileSuccess}</Alert>}

            <TextField
              fullWidth
              label="Name"
              name="name"
              value={profileData.name}
              onChange={handleProfileChange}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button type="submit" variant="contained" fullWidth disabled={profileSaving}>
                {profileSaving ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Contact Card */}
        <Paper sx={cardStyle}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            Contact Information
          </Typography>
          
          <Box sx={{ flex: 1 }}>
            {emailChangeSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEmailChangeSuccess('')}>
                {emailChangeSuccess}
              </Alert>
            )}
            {phoneChangeSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPhoneChangeSuccess('')}>
                {phoneChangeSuccess}
              </Alert>
            )}

            {/* Email */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Email Address
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                  {user?.email}
                </Typography>
                {user?.emailVerified && (
                  <Chip label="Verified" size="small" color="success" variant="outlined" />
                )}
                <IconButton size="small" onClick={() => setEmailDialogOpen(true)} title="Change email">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {pendingEmailChange && (
                <Alert 
                  severity="info" 
                  sx={{ mt: 1 }}
                  icon={<PendingIcon />}
                  action={
                    <Box>
                      <Button size="small" onClick={handleResendEmailVerification}>Resend</Button>
                      <Button size="small" color="error" onClick={handleCancelEmailChange}>Cancel</Button>
                    </Box>
                  }
                >
                  Pending: {pendingEmailChange.newValue}
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Phone */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Phone Number
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                  {user?.phone ? formatPhoneForDisplay(user.phone) : 'Not set'}
                </Typography>
                {user?.phoneVerified && (
                  <Chip label="Verified" size="small" color="success" variant="outlined" />
                )}
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setPhoneDialogOpen(true);
                    if (pendingPhoneChange) setPhoneStep('verify');
                  }}
                  title="Change phone"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {pendingPhoneChange && (
                <Alert 
                  severity="info" 
                  sx={{ mt: 1 }}
                  icon={<PendingIcon />}
                  action={<Button size="small" color="error" onClick={handleCancelPhoneChange}>Cancel</Button>}
                >
                  Pending: {pendingPhoneChange.newValue}
                </Alert>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Notifications Card */}
        <Paper sx={cardStyle}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon color="primary" />
            Notification Preferences
          </Typography>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {notificationError && <Alert severity="error" sx={{ mb: 2 }}>{notificationError}</Alert>}
            {notificationSuccess && <Alert severity="success" sx={{ mb: 2 }}>{notificationSuccess}</Alert>}

            {user?.accountType !== 'client_solo' && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Email Notifications
                </Typography>
                <List dense sx={{ flex: 1 }}>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary="High-risk alerts"
                      secondary="Get notified immediately when high-risk messages are detected"
                    />
                    <Switch
                      edge="end"
                      name="emailHighRisk"
                      checked={notificationPrefs.emailHighRisk}
                      onChange={handleNotificationChange}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary="Daily summary"
                      secondary="Receive a daily digest of all activity"
                    />
                    <Switch
                      edge="end"
                      name="emailDailySummary"
                      checked={notificationPrefs.emailDailySummary}
                      onChange={handleNotificationChange}
                    />
                  </ListItem>
                </List>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Incident Alerts
                </Typography>
                <List dense>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <WarningIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Money sent alerts"
                      secondary="Get notified when your loved one reports sending money"
                    />
                    <Switch
                      edge="end"
                      name="alertOnMoneySent"
                      checked={notificationPrefs.alertOnMoneySent}
                      onChange={handleNotificationChange}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <SecurityIcon sx={{ color: '#ea580c' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Personal information shared alerts"
                      secondary="Get notified when your loved one reports sharing personal information"
                    />
                    <Switch
                      edge="end"
                      name="alertOnPiiDisclosed"
                      checked={notificationPrefs.alertOnPiiDisclosed}
                      onChange={handleNotificationChange}
                    />
                  </ListItem>
                  {(notificationPrefs.alertOnMoneySent || notificationPrefs.alertOnPiiDisclosed) && (
                    <ListItem disablePadding sx={{ py: 0.5, pl: 4 }}>
                      <ListItemText
                        primary="Alert channel"
                        secondary={
                          notificationPrefs.alertChannel === 'both' ? 'SMS + Email' :
                          notificationPrefs.alertChannel === 'sms' ? 'SMS only' : 'Email only'
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {['both', 'sms', 'email'].map((ch) => (
                          <Button
                            key={ch}
                            size="small"
                            variant={notificationPrefs.alertChannel === ch ? 'contained' : 'outlined'}
                            onClick={() => {
                              setNotificationPrefs(prev => ({ ...prev, alertChannel: ch }));
                              setNotificationSuccess('');
                              setNotificationError('');
                            }}
                            sx={{ textTransform: 'capitalize', minWidth: 50, fontSize: '0.7rem' }}
                          >
                            {ch === 'both' ? 'Both' : ch.toUpperCase()}
                          </Button>
                        ))}
                      </Box>
                    </ListItem>
                  )}
                </List>

                {/* Per-client mute toggles (self users with clients) */}
                {user?.accountType === 'self' && clientList.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Per-Person Notifications
                    </Typography>
                    <List dense>
                      {clientList.map((client) => (
                        <ListItem key={client.id} disablePadding sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <PersonIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={client.name || client.email || 'Unknown'}
                            secondary={mutedClients.includes(client.id) ? 'Notifications muted' : 'Notifications on'}
                          />
                          <Switch
                            edge="end"
                            checked={!mutedClients.includes(client.id)}
                            onChange={() => handleMuteToggle(client.id)}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </>
            )}

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SummarizeIcon fontSize="small" color="primary" />
              Activity Summaries
            </Typography>
            <List dense>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText
                  primary="Send me activity summaries"
                  secondary="Periodic overview of messages, red flags, and training"
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={notificationPrefs.summaryFrequency}
                    onChange={(e) => {
                      setNotificationPrefs(prev => ({ ...prev, summaryFrequency: e.target.value }));
                      setNotificationSuccess('');
                      setNotificationError('');
                    }}
                  >
                    <MenuItem value="off">Off</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="biweekly">Bi-weekly</MenuItem>
                  </Select>
                </FormControl>
              </ListItem>
              {notificationPrefs.summaryFrequency !== 'off' && (
                <>
                  <ListItem disablePadding sx={{ py: 0.5, pl: 2 }}>
                    <ListItemText primary="Send via" />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {['email', 'sms', 'both'].map((ch) => (
                        <Button
                          key={ch}
                          size="small"
                          variant={notificationPrefs.summaryChannel === ch ? 'contained' : 'outlined'}
                          onClick={() => {
                            setNotificationPrefs(prev => ({ ...prev, summaryChannel: ch }));
                            setNotificationSuccess('');
                            setNotificationError('');
                          }}
                          sx={{ textTransform: 'capitalize', minWidth: 50, fontSize: '0.7rem' }}
                        >
                          {ch === 'both' ? 'Both' : ch.toUpperCase()}
                        </Button>
                      ))}
                    </Box>
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5, pl: 2 }}>
                    <ListItemText primary="Send on" />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={notificationPrefs.summaryDay}
                        onChange={(e) => {
                          setNotificationPrefs(prev => ({ ...prev, summaryDay: e.target.value }));
                          setNotificationSuccess('');
                          setNotificationError('');
                        }}
                      >
                        <MenuItem value={0}>Sunday</MenuItem>
                        <MenuItem value={1}>Monday</MenuItem>
                        <MenuItem value={2}>Tuesday</MenuItem>
                        <MenuItem value={3}>Wednesday</MenuItem>
                        <MenuItem value={4}>Thursday</MenuItem>
                        <MenuItem value={5}>Friday</MenuItem>
                        <MenuItem value={6}>Saturday</MenuItem>
                      </Select>
                    </FormControl>
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5, pl: 2 }}>
                    <ListItemText
                      primary="Send even if no activity"
                      secondary="Get a brief 'all clear' message when nothing happened"
                    />
                    <Switch
                      edge="end"
                      checked={notificationPrefs.summarySendIfNoActivity}
                      onChange={(e) => {
                        setNotificationPrefs(prev => ({ ...prev, summarySendIfNoActivity: e.target.checked }));
                        setNotificationSuccess('');
                        setNotificationError('');
                      }}
                    />
                  </ListItem>
                </>
              )}
            </List>

            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button variant="contained" fullWidth onClick={handleNotificationSubmit} disabled={notificationSaving}>
                {notificationSaving ? <CircularProgress size={24} /> : 'Save Preferences'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Billing Card — right column next to Notification Preferences */}
        <Paper sx={cardStyle}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon color="primary" />
            Billing & Subscription
          </Typography>

          {subscription ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, flex: '1 1 150px', minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary">Current Plan</Typography>
                  <Typography variant="h6">
                    ${subscription.quantity ? (subscription.quantity * 15).toFixed(2) : '0.00'}/month
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {subscription.quantity || 1} person{(subscription.quantity || 1) !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, flex: '1 1 150px', minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="h6" color={subscription.status === 'active' || subscription.status === 'trialing' ? 'success.main' : 'warning.main'}>
                    {subscription.status}
                  </Typography>
                  {subscription.trialEnd && new Date(subscription.trialEnd) > new Date() && (
                    <Typography variant="body2" color="warning.main">
                      Trial: {Math.ceil((new Date(subscription.trialEnd) - new Date()) / (1000 * 60 * 60 * 24))} days left
                    </Typography>
                  )}
                </Box>
                <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, flex: '1 1 150px', minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary">Usage</Typography>
                  <Typography variant="h6">{subscription.currentUsage || 0} / {subscription.quantity || 1}</Typography>
                  <Typography variant="body2" color="text.secondary">slots used</Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                To view billing history, download invoices, update payment method, or manage your subscription, click below to open the Stripe billing portal.
              </Typography>

              {billingError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBillingError('')}>
                  {billingError}
                </Alert>
              )}
              <Box sx={{ mt: 'auto' }}>
                <Button variant="contained" onClick={handleManageBilling} disabled={billingLoading}>
                  {billingLoading ? <CircularProgress size={24} /> : 'Manage Billing'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">No active subscription found.</Typography>
          )}
        </Paper>

        {/* Password Card - Full Width */}
        <Paper sx={{ ...cardStyle, gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon color="primary" />
            Change Password
          </Typography>

          <Box component="form" onSubmit={handlePasswordSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
            {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>}

            <TextField
              fullWidth
              label="Current Password"
              name="currentPassword"
              type={showCurrentPassword ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              size="small"
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon color="action" fontSize="small" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" size="small">
                      {showCurrentPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              size="small"
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon color="action" fontSize="small" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                      {showNewPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {passwordData.newPassword && (
              <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {passwordValidation.map((req, index) => (
                    <Chip
                      key={index}
                      label={req.label}
                      size="small"
                      color={req.met ? 'success' : 'default'}
                      variant={req.met ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              size="small"
              error={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
              helperText={
                passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon color="action" fontSize="small" /></InputAdornment>,
              }}
            />

            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button type="submit" variant="contained" fullWidth disabled={passwordSaving}>
                {passwordSaving ? <CircularProgress size={24} /> : 'Change Password'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Email Change Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => {
        if (!emailChangeSaving && emailStep !== 'code') {
          setEmailDialogOpen(false);
          resetEmailDialog();
        }
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {emailStep === 'input' && 'Change Email Address'}
          {emailStep === 'code' && 'Verify Your Identity'}
          {emailStep === 'done' && 'Check Your Email'}
        </DialogTitle>
        <DialogContent>
          {emailChangeError && <Alert severity="error" sx={{ mb: 2 }}>{emailChangeError}</Alert>}

          {emailStep === 'input' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                For security, we'll send a verification code to your phone before changing your email.
              </Typography>

              <TextField
                fullWidth
                label="New Email Address"
                value={newEmail}
                onChange={(e) => { setNewEmail(e.target.value); setEmailChangeError(''); }}
                type="email"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>,
                }}
              />

              <TextField
                fullWidth
                label="Current Password"
                value={emailPassword}
                onChange={(e) => { setEmailPassword(e.target.value); setEmailChangeError(''); }}
                type={showEmailPassword ? 'text' : 'password'}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowEmailPassword(!showEmailPassword)} edge="end">
                        {showEmailPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>How should we send the verification code?</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={emailDeliveryMethod === 'sms' ? 'contained' : 'outlined'}
                  startIcon={<SmsIcon />}
                  onClick={() => setEmailDeliveryMethod('sms')}
                  sx={{ flex: 1 }}
                >
                  Text me
                </Button>
                <Button
                  variant={emailDeliveryMethod === 'voice' ? 'contained' : 'outlined'}
                  startIcon={<CallIcon />}
                  onClick={() => setEmailDeliveryMethod('voice')}
                  sx={{ flex: 1 }}
                >
                  Call me
                </Button>
              </Box>
            </>
          )}

          {emailStep === 'code' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the 6-digit code {emailDeliveryMethod === 'sms' ? 'sent to' : 'called to'} {emailMaskedPhone}
              </Typography>

              <TextField
                fullWidth
                label="Verification Code"
                value={emailVerificationCode}
                onChange={(e) => {
                  setEmailVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setEmailChangeError('');
                }}
                placeholder="123456"
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
              />

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Didn't receive it?</Typography>
                <Button size="small" onClick={() => handleEmailResendCode('sms')} disabled={emailChangeSaving}>Text again</Button>
                <Button size="small" onClick={() => handleEmailResendCode('voice')} disabled={emailChangeSaving}>Call instead</Button>
              </Box>
            </>
          )}

          {emailStep === 'done' && (
            <Alert severity="success">
              We've sent a verification link to <strong>{newEmail}</strong>. Check your inbox and click the link to complete the change.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {emailStep !== 'done' && (
            <Button onClick={() => { setEmailDialogOpen(false); resetEmailDialog(); }}>Cancel</Button>
          )}
          {emailStep === 'input' && (
            <Button
              variant="contained"
              onClick={() => handleEmailChangeRequest()}
              disabled={emailChangeSaving || !newEmail || !emailPassword}
              startIcon={emailChangeSaving ? <CircularProgress size={16} /> : <SendIcon />}
            >
              Continue
            </Button>
          )}
          {emailStep === 'code' && (
            <Button
              variant="contained"
              onClick={handleEmailCodeVerify}
              disabled={emailChangeSaving || emailVerificationCode.length !== 6}
              startIcon={emailChangeSaving ? <CircularProgress size={16} /> : <CheckIcon />}
            >
              Verify
            </Button>
          )}
          {emailStep === 'done' && (
            <Button variant="contained" onClick={() => { setEmailDialogOpen(false); resetEmailDialog(); loadData(); }}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Phone Change Dialog */}
      <Dialog open={phoneDialogOpen} onClose={() => {
        if (!phoneChangeSaving) {
          setPhoneDialogOpen(false);
          if (!pendingPhoneChange) {
            setPhoneStep('request');
            setNewPhone('');
            setPhoneVerificationCode('');
            setPhoneChangeError('');
          }
        }
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{phoneStep === 'request' ? 'Change Phone Number' : 'Verify Phone Number'}</DialogTitle>
        <DialogContent>
          {phoneStep === 'request' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We'll send a verification code to your new phone number via SMS.
              </Typography>
              {phoneChangeError && <Alert severity="error" sx={{ mb: 2 }}>{phoneChangeError}</Alert>}
              <TextField
                fullWidth
                label="New Phone Number"
                value={newPhone}
                onChange={(e) => { setNewPhone(formatPhoneInput(e.target.value)); setPhoneChangeError(''); }}
                placeholder="(555) 123-4567"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PhoneIcon color="action" /></InputAdornment>,
                }}
              />
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the 6-digit code sent to your new phone number.
              </Typography>
              {phoneChangeError && <Alert severity="error" sx={{ mb: 2 }}>{phoneChangeError}</Alert>}
              <TextField
                fullWidth
                label="Verification Code"
                value={phoneVerificationCode}
                onChange={(e) => {
                  setPhoneVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setPhoneChangeError('');
                }}
                placeholder="123456"
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
              />
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button size="small" onClick={handleResendPhoneCode}>Resend Code</Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPhoneDialogOpen(false);
            if (!pendingPhoneChange) {
              setPhoneStep('request');
              setNewPhone('');
              setPhoneVerificationCode('');
              setPhoneChangeError('');
            }
          }}>
            Cancel
          </Button>
          {phoneStep === 'request' ? (
            <Button 
              variant="contained" 
              onClick={handlePhoneChangeRequest}
              disabled={phoneChangeSaving || !newPhone}
              startIcon={phoneChangeSaving ? <CircularProgress size={16} /> : <SendIcon />}
            >
              Send Code
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handlePhoneVerify}
              disabled={phoneChangeSaving || phoneVerificationCode.length !== 6}
              startIcon={phoneChangeSaving ? <CircularProgress size={16} /> : <CheckIcon />}
            >
              Verify
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Person Dialog (for client_solo users) */}
      <Dialog open={addPersonOpen} onClose={handleAddPersonClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add a Loved One</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name and provide a phone number, email address, or both. If a phone number is provided, we'll send a verification text. Email-only entries are added immediately.
          </Typography>

          {addPersonError && (
            <Alert severity="error" sx={{ mb: 2 }}>{addPersonError}</Alert>
          )}

          <TextField
            margin="normal"
            fullWidth
            label="Name"
            name="name"
            value={addPersonForm.name}
            onChange={handleAddPersonFormChange}
            disabled={addPersonSubmitting}
            required
            placeholder="e.g., Mom, Dad, Grandma"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Phone Number (optional)"
            name="phone"
            placeholder="(555) 555-5555"
            value={addPersonForm.phone}
            onChange={handleAddPersonFormChange}
            onBlur={handleAddPersonPhoneBlur}
            disabled={addPersonSubmitting}
            inputProps={{ maxLength: 14 }}
            error={!!addPersonPhoneError}
            helperText={addPersonPhoneError || 'They will receive a verification text message'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color={addPersonPhoneError ? 'error' : 'action'} />
                </InputAdornment>
              ),
              endAdornment: checkingPhone ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Email Address (optional)"
            name="email"
            type="email"
            placeholder="email@example.com"
            value={addPersonForm.email}
            onChange={handleAddPersonFormChange}
            onBlur={handleAddPersonEmailBlur}
            disabled={addPersonSubmitting}
            error={!!addPersonEmailError}
            helperText={addPersonEmailError || 'They can forward suspicious emails to check@secondclear.com'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color={addPersonEmailError ? 'error' : 'action'} />
                </InputAdornment>
              ),
              endAdornment: checkingEmail ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddPersonClose} disabled={addPersonSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddPersonSubmit}
            variant="contained"
            disabled={addPersonSubmitting || checkingPhone || checkingEmail || !!addPersonPhoneError || !!addPersonEmailError}
          >
            {addPersonSubmitting ? <CircularProgress size={24} /> : (
              addPersonForm.phone.trim() ? 'Send Verification' : 'Add'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Confirmation Dialog (for add person) */}
      <Dialog open={!!upgradeInfo} onClose={() => { setUpgradeInfo(null); setPendingData(null); }}>
        <DialogTitle>Subscription Upgrade Required</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {upgradeInfo?.message}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f7fa' }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Current plan:</Typography>
              <Typography fontWeight="bold">
                {upgradeInfo?.currentQuantity} {upgradeInfo?.currentQuantity === 1 ? 'person' : 'people'} — ${upgradeInfo?.currentMonthly}/month
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography color="text.secondary">New plan:</Typography>
              <Typography fontWeight="bold" color="primary">
                {upgradeInfo?.newQuantity} {upgradeInfo?.newQuantity === 1 ? 'person' : 'people'} — ${upgradeInfo?.newMonthly}/month
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUpgradeInfo(null); setPendingData(null); }}>
            Cancel
          </Button>
          <Button onClick={handleUpgradeConfirm} variant="contained" disabled={addPersonSubmitting}>
            {addPersonSubmitting ? <CircularProgress size={24} /> : 'Confirm & Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;